import { Button } from "$components/button/button";
import { Card, CardColors, CardTitle } from "$components/card";
import { makeStyles } from "$components/mui-styles";
import { emptyNonPharmaItem } from "$components/non-pharma-dispense/non-pharma-dispense-types";
import { NonPharmaItemRow } from "$components/non-pharma-dispense/non-pharma-item-row";
import { TextArea as RHFTextArea } from "$components/rhf-fields";
import { useAnalytics } from "$global-providers/analytics-provider/provider";
import { useConfig } from "$global-providers/config-provider/provider";
import { FeatureFlagGuard } from "$global-providers/feature-flags-provider/feature-flag-guard";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import {
  RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME,
  type InvestigationCatalogueItem as RxInvestigationCatalogueItem,
} from "$global-providers/rxdb-provider/collections/investigation-catalogue-item";
import {
  RXDB_SYMPTOM_COLLECTION_NAME,
  type Symptom as RxSymptom,
} from "$global-providers/rxdb-provider/collections/symptom";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { TERRITORY } from "$routes/_lib/constants";
import { useQuerystring } from "$routes/_lib/use-querystring";
import ValidationContext from "$routes/_lib/validation/validation-context";
import {
  AddPrescriptionCard,
  createDefaultPrescription,
} from "$routes/patient/_lib/add-prescription-card";
import useScrollToElement, {
  scrollToElement,
} from "$routes/patient/_lib/use-scroll-to-element";
import { useWeightAndAllergies } from "$routes/patient/_lib/use-weight-and-allergies";
import type { DrugGuidanceDeviation } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/medication-guidelines-checker/guidelines-checker";
import { useClinicalDecisionSupportService } from "$routes/patient/[:patientId]/_lib/clinical-decision-support-service/provider";
import {
  addRelevantFormInvestigationsToCDS,
  removeRelevantFormInvestigationsFromCDS,
} from "$routes/patient/[:patientId]/_lib/form-investigation-to-cds";
import { RecommendedActionsCard } from "$routes/patient/[:patientId]/_lib/recommended-investigations-card";
import { SymptomCaptureInput } from "$routes/patient/[:patientId]/_lib/symptom-capture-input";
import {
  L10N,
  translateI18nObject,
} from "$routes/patient/[:patientId]/_lib/translation-util";
import { AddInvestigationCard } from "$routes/patient/[:patientId]/investigations/_lib/add-investigation-card";
import { InvestigationCardField } from "$routes/patient/[:patientId]/investigations/_lib/investigation-card-field";
import { InvestigationSelect } from "$routes/patient/[:patientId]/investigations/_lib/investigation-select";
import {
  generateBlankInvestigation,
  generatePopulatedInvestigation,
  generateUpdatedInvestigation,
  generateUpdatedInvestigationFromRecommendation,
  type TranslatedInvestigationCatalogueItem,
} from "$routes/patient/[:patientId]/investigations/_lib/util";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, styled } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import {
  unstable_useBlocker as useBlocker,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import { v4 } from "uuid";
import type * as Yup from "yup";
import AddInvestigationButton from "../../_lib/add-investigation-button";
import AddPrescriptionButton from "../../_lib/add-prescription-button";
import AddSuppliesButton from "../../_lib/add-supplies-button";
import { ConsultationNavigationConfirmationDialog } from "../_lib/consultation-navigation-confirmation-dialog";
import { DiagnosesSection } from "../_lib/diagnoses-section";
import {
  defaultDiagnosis,
  defaultValues,
} from "../_lib/initial-form-default-values";
import { useConsultationFormContext } from "../_lib/provider";
import {
  type FormValues,
  useValidationSchema,
} from "../_lib/validation-schema";
import { linkToNewConsultationReview } from "../links";

// Performance traces
export const TRACE_NEW_CONSULTATION_PAGE_START =
  "new_consultation_page_load_start";
export const TRACE_NEW_CONSULTATION_PAGE_FINISH =
  "new_consultation_page_load_finish";
export const TRACE_NEW_CONSULTATION_PAGE_LOAD_SUCCESS =
  "new_consultation_page_load_success";

export enum InvestigationRecommendationLocation {
  INVESTIGATION_CARD = "INVESTIGATION_CARD",
  RECOMMENDED_ACTIONS_CARD = "RECOMMENDED_ACTIONS_CARD",
}

enum PrescriptionRecommendationLocation {
  PRESCRIPTION_CARD = "PRESCRIPTION_CARD",
}

export const DIAGNOSES_SECTION_ID = "diagnoses-section";

const useStyles = makeStyles((theme) => ({
  buttonSection: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    paddingTop: theme.spacing(2),
  },
}));

const Column = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const UlColumn = styled("ul")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StyledForm = styled("form")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

function useHandleSubmit(trackingId: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setValues } = useConsultationFormContext();

  return useCallback(
    (values) => {
      setValues(values);
      navigate(
        {
          pathname: linkToNewConsultationReview(),
          search: location.search,
        },
        { state: { trackingId } },
      );
    },
    [navigate, location, setValues, trackingId],
  );
}

export const Page = (): React.ReactElement => {
  const { patientUuid } = useQuerystring();
  const user = useUser();

  const {
    patientClinicalResources: {
      add: addClinicalResource,
      remove: removeClinicalResource,
      clearStaged: clearStagedResources,
    },
    recommendations,
  } = useClinicalDecisionSupportService();

  const { trackEvent } = useAnalytics();

  const { territory } = useConfig();
  const isInNigeria = territory === TERRITORY.NIGERIA;

  const [showConfirmNavigate, setShowConfirmNavigate] =
    useState<boolean>(false);

  const { result: catalogueItemsResult, isFetching: isCatalogueItemsFetching } =
    useRxData<RxInvestigationCatalogueItem>(
      RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME,
      (collection) => collection.find(),
    );
  const catalogueItems = catalogueItemsResult.map((item) => item._data);

  const { result: availableSymptoms, isFetching: isSymptomsFetching } =
    useRxData<RxSymptom>(RXDB_SYMPTOM_COLLECTION_NAME, (collection) =>
      collection.find(),
    );

  const {
    replicationState: {
      investigationCatalogueItem: { isPulling: isCatalogueItemsPulling },
      symptom: { isPulling: isSymptomsPulling },
    },
  } = useRxDBContext();

  const isCatalogueItemsLoading =
    catalogueItems.length === 0 ||
    isCatalogueItemsFetching ||
    isCatalogueItemsPulling;

  const isSymptomsLoading =
    availableSymptoms.length === 0 || isSymptomsFetching || isSymptomsPulling;

  const { values } = useConsultationFormContext();

  const { weight, allergies } = useWeightAndAllergies({
    patientId: patientUuid,
  });

  const validationSchema = useValidationSchema();

  const [trackingId] = useState(v4());

  const handleSubmit = useHandleSubmit(trackingId);
  const formMethods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: values ?? defaultValues(),
    values,
  });
  // necessary to destructure this value here, or the form proxy stops it getting updated
  const { isDirty } = formMethods.formState;
  const shouldBlockNavigate = isDirty || !!values;
  const blocker = useBlocker(({ nextLocation }) => {
    if (
      !shouldBlockNavigate ||
      nextLocation.pathname === linkToNewConsultationReview()
    ) {
      return false;
    }
    setShowConfirmNavigate(true);
    return shouldBlockNavigate;
  });

  const {
    append: appendPrescription,
    fields: prescriptions,
    remove: removePrescription,
  } = useFieldArray({
    name: "prescriptions",
    control: formMethods.control,
  });

  const investigationsFieldName = "investigations";
  const {
    append: appendInvestigation,
    fields: investigations,
    remove: removeInvestigation,
    update: updateInvestigation,
  } = useFieldArray({
    name: investigationsFieldName,
    control: formMethods.control,
  });

  const {
    append: appendNonPharmaItem,
    fields: nonPharmaItems,
    remove: removeNonPharmaItem,
  } = useFieldArray({
    name: "nonPharmaItems",
    control: formMethods.control,
  });

  const {
    append: appendSymptom,
    fields: symptoms,
    remove: removeSymptom,
  } = useFieldArray({
    name: "symptoms",
    control: formMethods.control,
  });

  const {
    fields: diagnoses,
    append: appendDiagnosis,
    remove: removeDiagnosis,
    update: updateDiagnosis,
  } = useFieldArray({
    name: "diagnosis",
    control: formMethods.control,
  });

  const translatedInvestigationList = useMemo(
    () =>
      (catalogueItems?.map((investigation) =>
        translateI18nObject(investigation, L10N.EN),
      ) ?? []) as unknown as Array<TranslatedInvestigationCatalogueItem>,
    [catalogueItems],
  );

  const classes = useStyles();
  const { formatMessage } = useIntl();

  useEffect(() => {
    for (const [index, recommendation] of recommendations.entries()) {
      trackEvent("Investigation RecommendationShown", {
        trackingId,
        patientId: patientUuid,
        recommendationId: recommendation.id,
        recommendationDiseaseName: recommendation.diseaseDisplayName,
        recommendationInvestigationLoincCodes: recommendation.investigations
          .map((investigation) => investigation.catalogueItemLoincCode)
          .join(", "),
        recommendationPosition: index + 1,
      });
    }
  }, [recommendations, trackEvent, trackingId, patientUuid]);

  useEffect(() => {
    const { unsubscribe } = formMethods.watch((data, { name }) => {
      if (
        name?.includes("investigations") &&
        name?.endsWith(".outcome.result")
      ) {
        const indexMatch = name?.match(/investigations\.(\d+)\./);
        if (indexMatch) {
          const index = parseInt(indexMatch[1]);
          const investigation = data.investigations?.[index];

          if (investigation?.catalogueItem) {
            addRelevantFormInvestigationsToCDS({
              formItem: investigation,
              userId: user.id,
              addClinicalResource,
            });
          }
        }
      }
    });

    return () => unsubscribe();
  }, [formMethods, addClinicalResource, user.id]);

  const [lastDiagnosisOnFocus, setLastDiagnosisOnFocus] = useState(false);
  const handleAddDiagnosisFromNudge = useCallback(() => {
    const currentDiagnoses = formMethods.getValues("diagnosis");
    const lastDiagnosis = currentDiagnoses[currentDiagnoses.length - 1];

    scrollToElement(DIAGNOSES_SECTION_ID);
    setLastDiagnosisOnFocus(true);
    if (!lastDiagnosis || lastDiagnosis?.codification.name === "") {
      removeDiagnosis(currentDiagnoses.length - 1);
    }
    appendDiagnosis(defaultDiagnosis());
    trackEvent("Prescription RecommendationSelected", {
      patientId: patientUuid,
      trackingId,
      recommendationLocation:
        PrescriptionRecommendationLocation.PRESCRIPTION_CARD,
    });
  }, [
    appendDiagnosis,
    removeDiagnosis,
    formMethods,
    patientUuid,
    trackEvent,
    trackingId,
  ]);

  type FormErrors = Yup.InferType<typeof validationSchema>;

  return (
    <ValidationContext.Provider value={{ validationSchema }}>
      <FormProvider {...formMethods}>
        <StyledForm
          name="InitialForm"
          data-testid="patientConsultationFormContainer"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
        >
          <Card color={CardColors.CONSULTATION}>
            <ConsultationNavigationConfirmationDialog
              onCancel={() => setShowConfirmNavigate(false)}
              onContinue={() => {
                clearStagedResources();
                blocker.proceed();
              }}
              open={showConfirmNavigate}
            />

            <CardTitle>
              <FormattedMessage
                id="patient.new.consultation.form.title"
                defaultMessage="Consultation"
              />
            </CardTitle>

            <SymptomCaptureInput
              availableSymptoms={availableSymptoms}
              symptoms={symptoms}
              addChip={(chip) => {
                appendSymptom(chip);
                trackEvent("Symptom RecommendationSelected", {
                  trackingId,
                  patientId: patientUuid,
                  symptomId: chip.optionId,
                  symptomName: chip.label,
                });
              }}
              removeChip={removeSymptom}
              loading={isSymptomsLoading}
              patientId={patientUuid}
              resetInput={async () => {
                await clearStagedResources();
                removeSymptom();
              }}
            />

            <RHFTextArea
              name="problem"
              label={formatMessage({
                id: "patient.new.consultation.form.problem.label",
                defaultMessage: "History and examination",
              })}
              placeholder={
                isInNigeria
                  ? formatMessage(
                      {
                        id: "patient.new.consultation.form.problem.placeholder",
                        defaultMessage:
                          "Use this area to record:{newline}{newline}History{newline}Examination findings",
                      },
                      {
                        newline: "\n",
                      },
                    )
                  : formatMessage(
                      {
                        id: "patient.new.consultation.form.problem.placeholder.includePresentingComplaint",
                        defaultMessage:
                          "Use this area to record:{newline}{newline}Presenting complaint{newline}History{newline}Examination findings",
                      },
                      {
                        newline: "\n",
                      },
                    )
              }
              maxRows={4}
            />

            <Box id={DIAGNOSES_SECTION_ID} paddingY={1}>
              <DiagnosesSection
                diagnoses={diagnoses}
                append={appendDiagnosis}
                remove={removeDiagnosis}
                update={updateDiagnosis}
                shouldFocusLastDiagnosis={lastDiagnosisOnFocus}
              />
            </Box>

            <RHFTextArea
              name="plan"
              label={formatMessage({
                id: "patient.new.consultation.form.plan.label",
                defaultMessage: "What's the plan?",
              })}
              maxRows={4}
            />
          </Card>

          {/* Double-gate on the LD flag AND row presence so a draft restore
              from before the flag rollout can't surface the supplies card
              if the flag has since been turned off for this user. */}
          <FeatureFlagGuard
            flagKey={featureFlags.clinical.nonPharma}
            render={() =>
              nonPharmaItems.length > 0 ? (
                <Card color={CardColors.CONSULTATION}>
                  <CardTitle>
                    <FormattedMessage
                      id="patient.new.consultation.form.nonPharma.title"
                      defaultMessage="Supplies used"
                    />
                  </CardTitle>
                  {nonPharmaItems.map((field, index) => (
                    <NonPharmaItemRow
                      key={field.id}
                      fieldName={`nonPharmaItems.${index}`}
                      locationId={null}
                      onDelete={() => removeNonPharmaItem(index)}
                    />
                  ))}
                </Card>
              ) : (
                <></>
              )
            }
          />

          {prescriptions.length > 0 ? (
            <Column>
              <UlColumn>
                {prescriptions.map((field, index) => (
                  <ScrollTo elementId={field.id}>
                    <li key={field.id} id={field.id}>
                      <AddPrescriptionCard
                        patientWeight={weight ? `${weight}` : null}
                        patientAllergies={allergies}
                        onDelete={() => removePrescription(index)}
                        onAddDiagnosis={handleAddDiagnosisFromNudge}
                        trackRecommendationShown={(
                          recommendationVariant: DrugGuidanceDeviation,
                        ) => {
                          trackEvent("Prescription RecommendationShown", {
                            trackingId,
                            patientId: patientUuid,
                            recommendationVariant,
                          });
                        }}
                        fieldNames={{
                          clinicalProductId: `prescriptions.${index}.clinicalProductId`,
                          clinicalProductType: `prescriptions.${index}.clinicalProductType`,
                          freeTextProduct: `prescriptions.${index}.freeTextProduct`,
                          howItsTaken: `prescriptions.${index}.howItsTaken`,
                          doseValue: `prescriptions.${index}.doseValue`,
                          doseUnit: `prescriptions.${index}.doseUnit`,
                          rateValue: `prescriptions.${index}.rateValue`,
                          rateUnit: `prescriptions.${index}.rateUnit`,
                          howLongForValue: `prescriptions.${index}.howLongForValue`,
                          howLongForUnit: `prescriptions.${index}.howLongForUnit`,
                          note: `prescriptions.${index}.note`,
                        }}
                      />
                    </li>
                  </ScrollTo>
                ))}
              </UlColumn>
            </Column>
          ) : null}

          {investigations.length > 0 ? (
            <Column data-testid="consultationInvestigationContainer">
              {investigations.map((item, index) => (
                <AddInvestigationCard
                  key={item.id}
                  hasSelectedInvestigation={!!item?.catalogueItem}
                  recommendedInvestigations={recommendations.flatMap(
                    (recommendation) => recommendation.investigations,
                  )}
                  onRecommendedClick={(value) => {
                    const investigation =
                      generateUpdatedInvestigationFromRecommendation({
                        recommendedInvestigation: value,
                        translatedInvestigationList,
                        formInvestigation: investigations[index],
                      });
                    updateInvestigation(index, investigation);
                    trackEvent("Investigation RecommendationSelected", {
                      recommendationInvestigation: value.displayName,
                      recommendationCatalogueItemLoincCode:
                        value.catalogueItemLoincCode,
                      recommendationCode: value.code,
                      recommendationLocation:
                        InvestigationRecommendationLocation.INVESTIGATION_CARD,
                      patientId: patientUuid,
                      recommendationIndex: index + 1,
                      trackingId,
                    });
                  }}
                  onDelete={() => {
                    removeInvestigation(index);
                    if (item?.catalogueItem) {
                      removeRelevantFormInvestigationsFromCDS({
                        formItem: item,
                        removeItem: removeClinicalResource,
                      });
                    }
                  }}
                >
                  <InvestigationSelect
                    investigationList={translatedInvestigationList}
                    // TODO: fix the inferred type as it doesn't know the shape of the form
                    error={
                      (formMethods.formState.errors as unknown as FormErrors)
                        ?.investigations?.[index]?.catalogueItem?.message
                    }
                    onChange={(value) => {
                      const item = investigations[index];
                      if (
                        item?.catalogueItem &&
                        item?.catalogueItem.id !== value.id
                      ) {
                        removeRelevantFormInvestigationsFromCDS({
                          formItem: item,
                          removeItem: removeClinicalResource,
                        });
                      }

                      if (value) {
                        updateInvestigation(
                          index,
                          generateUpdatedInvestigation({
                            formInvestigation: investigations[index],
                            catalogueItem: value,
                          }),
                        );
                      } else {
                        updateInvestigation(
                          index,
                          generateBlankInvestigation(investigations[index].id),
                        );
                      }
                      formMethods.trigger("investigations");
                    }}
                    value={investigations[index]?.catalogueItem || null}
                    loading={isCatalogueItemsLoading}
                    onClear={() => {
                      const item = investigations[index];
                      if (item?.catalogueItem) {
                        removeRelevantFormInvestigationsFromCDS({
                          formItem: item,
                          removeItem: removeClinicalResource,
                        });
                        updateInvestigation(
                          index,
                          generateBlankInvestigation(investigations[index].id),
                        );
                      }
                    }}
                  />
                  {!!item?.catalogueItem && (
                    <>
                      <Divider />
                      <InvestigationCardField
                        catalogueItem={item.catalogueItem}
                        key={item.id}
                        index={index}
                        fieldNames={{
                          investigations: "investigations",
                        }}
                        onInvestigationChanged={(formItem) => {
                          addRelevantFormInvestigationsToCDS({
                            formItem,
                            addClinicalResource,
                            userId: user.id,
                          });
                        }}
                      />
                    </>
                  )}
                </AddInvestigationCard>
              ))}
            </Column>
          ) : null}

          {!!recommendations.length && (
            <Column>
              <RecommendedActionsCard
                trackingId={trackingId}
                patientId={patientUuid}
                diseasesRecommendations={recommendations}
                onAddInvestigation={(catalogueItemLoincCode: string) => {
                  const catalogueItem = translatedInvestigationList.find(
                    (catalogueItem) =>
                      catalogueItem.coding.loinc?.code ===
                      catalogueItemLoincCode,
                  );
                  appendInvestigation(
                    generatePopulatedInvestigation(catalogueItem),
                    {
                      shouldFocus: false,
                    },
                  );
                }}
              />
            </Column>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            flexWrap="wrap"
            gridGap="10px"
            mb={2}
            className={classes.buttonSection}
          >
            <AddInvestigationButton
              onClick={() => appendInvestigation(generateBlankInvestigation())}
            />

            <AddPrescriptionButton
              onClick={() => {
                appendPrescription(createDefaultPrescription());
              }}
            />
            <FeatureFlagGuard
              flagKey={featureFlags.clinical.nonPharma}
              render={() => (
                <AddSuppliesButton
                  onClick={() =>
                    appendNonPharmaItem(
                      // Cast: the Yup-inferred form type pins quantity to
                      // `number`, but `NonPharmaItemFormValue` widens it
                      // to `number | string` for RHFTextField round-tripping.
                      emptyNonPharmaItem() as FormValues["nonPharmaItems"][number],
                    )
                  }
                />
              )}
            />
            <Button variant="contained" color="primary" type="submit">
              <FormattedMessage
                id="patient.new.consultation.reviewBtn"
                defaultMessage="Review"
              />
            </Button>
          </Box>
        </StyledForm>
      </FormProvider>
    </ValidationContext.Provider>
  );
};

const ScrollTo: FCwC<{ elementId: string }> = ({ elementId, children }) => {
  useScrollToElement(elementId);

  return <>{children}</>;
};
